
/**
 * 전자 결재선(사람 수 자유) → <b>서면 품의서 결재란(칸 고정)</b> 배치 (DOC-51 §3).
 *
 * 사내 결재는 두 겹이다 — 팀장까지는 전자로 끝나고, 본부장·대표이사는 종이로 올라간다.
 * 그래서 이 표의 <b>빈 칸이 곧 "여기부터 서면"</b> 이라는 표시다. 전자결재선에 그 사람이
 * 없어도 본부장·대표이사 칸은 <b>언제나 그린다</b>.
 *
 * 규칙의 첫 번째 의무는 <b>사람을 버리지 않는 것</b>이다 — 결재한 사람이 종이에서 사라지면
 * 그 종이는 증빙이 아니다. 직함으로 칸을 못 찾은 결재자는 팀장 칸(비어 있으면) 또는 협조 칸으로 간다.
 */

/**
 * 결재란 한 칸에 앉을 <b>결재선 한 줄</b>.
 *
 * <p>여기 있는 이유: 이 형은 {@link paperBoxes} 의 입력이고, 그 규칙을 웹 화면·앱 기안문·PDF 가
 * 함께 쓴다. 종전에는 웹 컴포넌트({@code ApprovalDocumentView})가 들고 있었는데, 그 파일은 JSX 라
 * <b>형 하나를 가져오려다 앱의 타입 검사가 웹 화면 전체를 끌고 왔다</b>(2026-09-28).
 */
export type ApprovalBox = {
	/** 기안(담당)·결재·합의(협조)·참조. {@code REFERENCE} 는 결재란에 그리지 않는다. */
	kind: 'DRAFT' | 'APPROVAL' | 'AGREEMENT' | 'REFERENCE';
	/** 직함 — 어느 칸(대표이사/본부장/팀장)에 앉을지 정하는 값이다. */
	position: string | null;
	name: string | null;
	/** 도장: 승인 / 합의 / 대결 / 반려. null 이면 미처리(빈 칸으로 인쇄해 손서명). */
	signature: string | null;
	/** 처리일(ISO). 도장 아래 `M.D.` 로 찍힌다. */
	decidedAt?: string | null;
	/** 그 칸에 찍히는 서명 그림(PNG data URL · V328). */
	signImage?: string | null;
	/** <b>이 줄에서 문서가 끝난다</b>(전결 지점). */
	final?: boolean;
	/** 전결로 <b>생략된</b> 줄. 그 사람은 이 문서를 보지 않았으므로 결재란에 서지 않는다. */
	skipped?: boolean;
};

export type PaperCell = {
  /**
   * 그 칸에 <b>이름 자리</b>로 적히는 말. 사람 이름이거나, 전결자가 비우고 간 칸의 「전결」이다
   * ({@link signsForCeo}) — 둘 다 「이 칸을 누가 맡았나」 를 말하므로 같은 글씨다.
   */
  name: string | null;
  /** 승인/합의/대결/반려. null 이면 미처리 — 빈 칸으로 인쇄해 손서명한다. */
  stamp: string | null;
  /** 처리일(ISO). 도장 아래 `M.D.` 로 찍힌다. */
  decidedAt?: string | null;
  /** 그 칸에 찍히는 서명 그림(PNG data URL · V328). 없으면 종전대로 도장 글씨만 선다. */
  signImage?: string | null;
};

export type PaperBoxes = {
  /** 대표이사 — 전자결재선에 없으면 비어 있다(서면). */
  ceo: PaperCell;
  /** 본부장 — 전자결재선에 없으면 비어 있다(서면). */
  division: PaperCell;
  team: PaperCell;
  staff: PaperCell;
  /** 협조 = 합의 라인 + 칸을 못 찾은 결재자. */
  coop: PaperCell[];
};

const CEO_WORDS = ['대표이사', '대표 이사', '사장', '회장'];
const DIVISION_WORDS = ['본부장', '부문장', '전무', '상무', '이사'];
const TEAM_WORDS = ['팀장', '실장', '파트장', '지사장', '소장'];

const EMPTY: PaperCell = { name: null, stamp: null };

export function paperBoxes(boxes: ApprovalBox[]): PaperBoxes {
  const result: PaperBoxes = { ceo: { ...EMPTY }, division: { ...EMPTY }, team: { ...EMPTY }, staff: { ...EMPTY }, coop: [] };

  for (const box of boxes) {
    if (box.kind === 'REFERENCE') continue; // 참조자는 결재란이 아니라 머리의 '참 조:' 줄에 선다.
    /*
      전결로 생략된 줄은 <b>종이에 서지 않는다</b>. 그 사람은 이 문서를 보지 않았고, 그 칸은
      이미 전결자가 맡았다({@link signsForCeo}) — 이름을 적으면 결재하지 않은 사람이
      결재란에 있는 종이가 되고, 협조 칸으로 흘려보내면 «합의한 사람»이 하나 늘어난다.
    */
    if (box.skipped) continue;
    const cell: PaperCell = {
      name: box.name,
      stamp: box.signature,
      decidedAt: box.decidedAt ?? null,
      signImage: box.signImage ?? null,
    };

    if (box.kind === 'DRAFT') {
      result.staff = cell;
      continue;
    }
    if (box.kind === 'AGREEMENT') {
      result.coop.push(cell);
      continue;
    }

    const slot = slotOf(box.position);
    if (signsForCeo(box, slot, result)) {
      result.ceo = cell;
      // 자기 직함 칸에는 「전결」만 남는다 — 그 자리를 비우고 위 칸에 서명했다는 표시다.
      const own = slot ?? 'team';
      if (!result[own].name) result[own] = { ...DELEGATED_MARK };
      continue;
    }

    if (slot && !result[slot].name) {
      result[slot] = cell;
      continue;
    }
    // 직함으로 못 가른 결재자(예: '부장')는 비어 있는 팀장 칸으로, 이미 찼으면 협조로.
    if (!slot && !result.team.name) result.team = cell;
    else result.coop.push(cell);
  }

  return result;
}

/**
 * 전결자가 <b>자기 칸을 비우고 대표이사 칸에 서명</b>하는가 (사용자 지시 2026-09-15:
 * 「팀장 전결이면 팀장 칸에는 전결 써두고, 대표이사 칸에 팀장이 서명하는 것」).
 *
 * <h2>왜 위 칸에 서명하는가</h2>
 * 전결은 <b>남의 결재권을 대신 쓰는 일</b>이다. 팀장이 팀장 자격으로 한 번 더 결재하는 것이
 * 아니라, 대표이사가 할 판단을 팀장이 한 것이다. 그래서 서명은 <b>그 권한이 있던 칸</b>에
 * 들어가고, 팀장 칸에는 「전결」 두 글자만 남아 <b>어디로 갔는지</b>를 가리킨다.
 *
 * <p>같은 이름을 두 칸에 적지 않는다 — 한 사람이 두 번 결재한 종이가 된다.
 *
 * <h2>안 하는 경우</h2>
 * <ul>
 *   <li><b>대표이사가 곧 전결자</b>면 옮길 데가 없다. 자기 권한으로 자기 칸에 결재한 것이라
 *       「전결」도 붙이지 않는다 — 붙이면 누구에게 위임받았다는 말이 되어 사실과 어긋난다.</li>
 *   <li><b>대표이사 칸이 이미 찼으면</b>(전결 뒤 서면으로 더 올리는 구성) 건드리지 않는다.
 *       그 칸의 주인을 밀어내면 결재한 사람이 종이에서 사라진다.</li>
 * </ul>
 *
 * <p>승인을 <b>기다리지 않는다.</b> 이 배치는 도장이 아니라 결재란의 구성이다 — 결재선에 전결
 * 줄이 선 순간 그 문서는 전결로 끝나는 문서이고, 기안자가 미리보기에서 보는 종이도 그래야 한다.
 * 도장(처리일)은 그 사람이 실제로 누른 뒤에 붙는다.
 */
function signsForCeo(box: ApprovalBox, slot: ReturnType<typeof slotOf>, result: PaperBoxes): boolean {
  return !!box.final && slot !== 'ceo' && !result.ceo.name;
}

/** 전결자가 비우고 간 자리에 남는 말. 도장이 아니라 <b>이름 자리</b>다 — 검은 글씨로 선다. */
const DELEGATED_MARK: PaperCell = { name: '전결', stamp: null, decidedAt: null };

/** 직함 문자열 → 칸. 전결 표기('팀장 전결')가 붙어 있어도 포함 검사라 그대로 걸린다. */
function slotOf(position: string | null): 'ceo' | 'division' | 'team' | null {
  const text = position ?? '';
  if (CEO_WORDS.some((w) => text.includes(w))) return 'ceo';
  if (DIVISION_WORDS.some((w) => text.includes(w))) return 'division';
  if (TEAM_WORDS.some((w) => text.includes(w))) return 'team';
  return null;
}

/** 서면 문서로 뽑을 수 있는 상태인지 — 상신된 문서만 종이로 올라간다(번호가 있어야 한다). */
export function printableAsPaper(docNo: string | null | undefined, submittedAt: string | null | undefined): boolean {
  return !!docNo && !!submittedAt;
}

/** 사내 양식의 시행일자 표기 — `2026. 08. 10.` (한 자리 달·일도 두 자리로 채운다). */
export function paperDate(value: string | Date | null | undefined): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}. ${pad(date.getMonth() + 1)}. ${pad(date.getDate())}.`;
}

/** 결재란 도장 아래 붙는 처리일 — `8. 12.` 처럼 짧게. */
export function stampDate(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getMonth() + 1}. ${date.getDate()}.`;
}
